import React, { useRef, useEffect } from "react";
import AppIcon from "@/components/AppIcon";

interface AppTextAreaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "onChange"> {
  value: string;
  onChange: (value: string) => void;
  onSend: () => void;
}

const AppTextArea: React.FC<AppTextAreaProps> = ({
  value,
  onChange,
  onSend,
  ...props
}) => {
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto"; // Reset height to auto for calculation
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`; // Adjust height
    }
  }, [value]);

  const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange(event.target.value);
  };

  const handleSend = () => {
    if (value.trim()) {
      onSend();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      // Prevent default Enter behavior (e.g., adding a new line)
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="relative w-full">
      <textarea
        ref={textareaRef}
        className="block w-full px-3 pr-12 py-2 text-sm text-gray-900 border border-green-500 rounded-md shadow-sm focus:outline-none focus:border-green-600 focus:ring-1 focus:ring-green-600 resize-none overflow-auto transition-all"
        style={{
          maxHeight: "120px",
        }}
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        {...props}
      />
      <button
        type="button"
        onClick={handleSend}
        className="absolute top-1/2 right-2 transform -translate-y-1/2 flex items-center justify-center w-8 h-8 bg-white rounded-mdfocus:outline-none"
      >
        <AppIcon name="send" className="text-primary hover:text-green-600" />
      </button>
    </div>
  );
};

export default AppTextArea;